/**
 * 営業ファネルダッシュボード（GR-04c-4）。
 *
 * Slack 日報（毎朝 9:00 配信・沖中設計）で役員に定着した読み方を画面へ移植する:
 *  - 30 秒レイヤー: 📅結論 → 🔥/🏷注目 → ⚠アラート（異常なしも明示発話）の順序固定
 *  - 確報と速報の分離: 最終完了月 = 確報、当月 = 📋速報（部分月を確報と誤読させない）
 *  - 矢印ファネル 1 行表示 + カッコ率、分母 0 は「-%」（0% と誤読させない）
 *  - 前月比は「+N / -N / ±0」、比較先なしは「-」（変化なしと未計測を区別）
 *  - 商材はファミリ集約（枝番除去）+ topN + その他合算（総数を欠落させない）
 *  - 数式・分母・対象範囲を脚注で毎回定義し直す（前提の読み飛ばし防止）
 *  - 鮮度 48h 超は fail-loud 警告（silent failure を毎朝可視化する思想と同じ）
 *
 * データ源は cb_funnel_snapshots / cb_policy_events（集計値のみ・掌握≠複製）。
 * 表示計算は lib/funnel-view.ts の純関数に分離。
 */
import { useMemo, useState } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { FreshnessRibbon } from '@/components/FreshnessRibbon'
import { GlossaryTerm } from '@/components/GlossaryTerm'
import {
  useFunnelOverview,
  useFunnelPolicyEvents,
  useFunnelSegments,
} from '@/hooks/useFunnel'
import type { FunnelSegmentAxis, FunnelStageCounts } from '@/lib/api'
import {
  aggregateFamilies,
  buildAlertLine,
  buildFunnelLine,
  buildMonthlyTable,
  buildTopHighlights,
  fmtCount,
  momDelta,
  okPct,
  paceProjection,
  shortMonthLabel,
  splitConfirmedAndPartial,
  STAGE_DEFS,
} from '@/lib/funnel-view'
import { cn } from '@/lib/utils'

const AXIS_TABS: ReadonlyArray<{ axis: FunnelSegmentAxis; label: string }> = [
  { axis: 'product', label: '商材' },
  { axis: 'owner', label: '担当者' },
]

// ─── 小物 ───

function LoadingRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-muted-foreground">
      <RefreshCw className="h-4 w-4 animate-spin" />
      {label}を読み込み中…
    </div>
  )
}

function ErrorRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-red-600">
      <AlertCircle className="h-4 w-4" />
      {label}の取得に失敗しました
    </div>
  )
}

function SectionCard({
  title,
  children,
  aside,
  note,
}: {
  title: string
  children: React.ReactNode
  aside?: React.ReactNode
  note?: React.ReactNode
}) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {aside}
      </div>
      {children}
      {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
    </div>
  )
}

/** 前月比チップ（+緑 / -赤 / ±0・- はグレー）。 */
function DeltaChip({ delta }: { delta: string }) {
  const tone = delta.startsWith('+')
    ? 'bg-emerald-500/15 text-emerald-300'
    : delta.startsWith('-') && delta !== '-'
      ? 'bg-red-500/15 text-red-300'
      : 'bg-slate-500/20 text-slate-400'
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums',
        tone,
      )}
    >
      {delta === '-' ? '前月比 -' : `前月比 ${delta}`}
    </span>
  )
}

/** 矢印ファネル 1 行（受注3,898→申込740(19.0%)→…）。日報の署名的フォーマット。 */
function FunnelLineRow({
  label,
  counts,
  indent = false,
}: {
  label: string
  counts: FunnelStageCounts
  indent?: boolean
}) {
  const steps = buildFunnelLine(counts)
  return (
    <div className="flex items-baseline gap-0 whitespace-nowrap font-mono text-[13px] leading-7 tabular-nums">
      <span
        className={cn(
          'inline-block w-28 shrink-0 truncate pr-2 text-slate-300',
          indent && 'pl-3',
        )}
      >
        {indent ? `･${label}` : label}
      </span>
      {steps.map((s, i) => (
        <span key={s.label} className="shrink-0">
          {i > 0 && <span className="text-slate-600">→</span>}
          <span className="text-slate-400">
            <GlossaryTerm term={s.label}>{s.label}</GlossaryTerm>
          </span>
          <span className="ml-0.5 font-semibold text-slate-50">{fmtCount(s.count)}</span>
          {s.rate !== null && <span className="text-slate-500">({s.rate})</span>}
        </span>
      ))}
    </div>
  )
}

// ─── ページ本体 ───

export function FunnelDashboardPage() {
  const [months, setMonths] = useState<6 | 12>(6)
  const [axis, setAxis] = useState<FunnelSegmentAxis>('product')
  const [drillProduct, setDrillProduct] = useState<string | null>(null)
  const [tableView, setTableView] = useState<'keiei' | 'bunseki'>('keiei')

  const overviewQuery = useFunnelOverview(months)
  const segmentsQuery = useFunnelSegments(axis, months)
  const productSegmentsQuery = useFunnelSegments('product', months)
  const listSegmentsQuery = useFunnelSegments('product_list', months, drillProduct !== null)
  const policyEventsQuery = useFunnelPolicyEvents(12)

  const overview = overviewQuery.data
  const periods = overview?.periods ?? []
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null)

  const now = useMemo(() => new Date(), [])

  // 確報 / 速報 / 前月（latestIsPartial の切り分け）
  const { confirmed, partial, previous } = useMemo(
    () => splitConfirmedAndPartial(overview?.months ?? []),
    [overview],
  )

  // ⚠ アラート行（正常 / 警告 / 判定なし の三値。無警告の沈黙を許さない）
  const alert = useMemo(
    () => buildAlertLine(overview?.computedAt ?? null, now),
    [overview, now],
  )

  // 📈 月末ペース換算（日報の着地見込とは別式のため名前を変えている）
  const pace = useMemo(() => paceProjection(partial, now), [partial, now])

  // 商材ファミリ集約（速報月の keiei 行を束ねる）
  const families = useMemo(() => {
    if (!partial) return []
    const rows =
      productSegmentsQuery.data?.rows.filter((r) => r.period === partial.period) ?? []
    return aggregateFamilies(rows, 5)
  }, [productSegmentsQuery.data, partial])

  // 🏷 両極端ハイライト（受注トップ / 離脱トップの 2 点だけ）
  const highlights = useMemo(() => buildTopHighlights(families), [families])

  // ステージ内訳の対象月（既定 = データのある最新月）
  const effectivePeriod = useMemo(() => {
    if (selectedPeriod && periods.includes(selectedPeriod)) return selectedPeriod
    return partial?.period ?? periods.at(-1) ?? null
  }, [selectedPeriod, periods, partial])

  const trendData = useMemo(
    () =>
      overview?.months.map((m) => ({
        period: m.period,
        受注: m.keiei?.stageCounts.juchu ?? null,
        開通: m.keiei?.stageCounts.activated ?? null,
        入金: m.keiei?.stageCounts.paid ?? null,
      })) ?? [],
    [overview],
  )

  const stageData = useMemo(() => {
    const month = overview?.months.find((m) => m.period === effectivePeriod)
    if (!month) return []
    return STAGE_DEFS.map((s) => ({
      stage: s.label,
      当月ベース: month.keiei?.stageCounts[s.key] ?? 0,
      受注月ベース: month.bunseki?.stageCounts[s.key] ?? 0,
    }))
  }, [overview, effectivePeriod])

  const monthlyTable = useMemo(
    () => (overview ? buildMonthlyTable(overview, tableView) : null),
    [overview, tableView],
  )

  const segmentRows = useMemo(() => {
    const rows = segmentsQuery.data?.rows.filter((r) => r.period === effectivePeriod) ?? []
    return rows
      .filter((r) => Object.values(r.stageCounts).some((v) => v > 0)) // ノイズ行除去
      .map((r) => ({
        segmentKey: r.segmentKey,
        stageCounts: r.stageCounts,
        name: axis === 'owner' ? `担当者 ${r.ownerId ?? '?'}` : (r.product ?? r.segmentKey),
      }))
  }, [segmentsQuery.data, effectivePeriod, axis])

  const drillRows = useMemo(() => {
    if (drillProduct === null) return []
    const rows =
      listSegmentsQuery.data?.rows.filter(
        (r) => r.period === effectivePeriod && r.product === drillProduct,
      ) ?? []
    return rows
      .filter((r) => Object.values(r.stageCounts).some((v) => v > 0))
      .map((r) => ({
        segmentKey: r.segmentKey,
        stageCounts: r.stageCounts,
        name: r.list ?? '(未設定)',
      }))
  }, [listSegmentsQuery.data, effectivePeriod, drillProduct])

  const policyEvents = policyEventsQuery.data?.events ?? []
  const eventPeriods = useMemo(
    () => new Set(policyEvents.map((e) => e.eventDate.slice(0, 7))),
    [policyEvents],
  )

  const asOfLabel = overview?.computedAt
    ? new Date(overview.computedAt).toLocaleString('ja-JP', {
        timeZone: 'Asia/Tokyo',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <div className="space-y-6">
      {/* ヘッダー（日報の 3 行固定ヘッダーと同じ語彙: 種別 / 基準 / 対象） */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">営業ファネル</h1>
        <p className="text-sm text-muted-foreground">
          {asOfLabel ? `基準: ${asOfLabel}` : '基準時刻 取得中'} ・{' '}
          <span className="whitespace-nowrap">直近{months}ヶ月</span> / 月次
        </p>
      </div>

      <FreshnessRibbon kinds={['funnel']} />

      {/* ⚠ アラート行 — 異常なしでも必ず 1 行出す（無警告の沈黙を許さない） */}
      <div
        className={cn(
          'rounded-md border px-3 py-2 text-sm font-medium',
          alert.tone === 'ok' && 'border-emerald-200 bg-emerald-50 text-emerald-800',
          alert.tone === 'warn' && 'border-red-300 bg-red-50 text-red-800',
          alert.tone === 'unknown' && 'border-amber-300 bg-amber-50 text-amber-800',
        )}
        data-testid="funnel-alert-line"
      >
        {alert.text}
      </div>

      <div className="flex items-center gap-2">
        {([6, 12] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMonths(m)}
            className={cn(
              'whitespace-nowrap rounded-md border px-3 py-1 text-sm',
              months === m ? 'border-blue-600 bg-blue-50 text-blue-700' : 'text-muted-foreground',
            )}
          >
            直近{m}ヶ月
          </button>
        ))}
      </div>

      {/* 朝礼ボード（ダーク面）: 30 秒で「ペース大丈夫?」に答える確報+速報+ファネル */}
      <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950 p-4 text-slate-100 shadow-lg">
        {overviewQuery.isLoading ? (
          <div className="flex items-center gap-2 py-8 text-slate-400">
            <RefreshCw className="h-4 w-4 animate-spin" />
            集計を読み込み中…
          </div>
        ) : overviewQuery.error ? (
          <div className="flex items-center gap-2 py-8 text-red-400">
            <AlertCircle className="h-4 w-4" />
            集計の取得に失敗しました
          </div>
        ) : (
          <div className="space-y-4">
            {/* 📅 確報（最終完了月）: 結論を先に */}
            {confirmed?.keiei && (
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-widest text-slate-500">
                  📅 {shortMonthLabel(confirmed.period)}確報
                </p>
                <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
                  {(
                    [
                      ['受注', 'juchu'],
                      ['開通', 'activated'],
                      ['入金', 'paid'],
                    ] as const
                  ).map(([label, key]) => (
                    <div key={key} className="flex items-baseline gap-2">
                      <span className="text-sm text-slate-400">{label}</span>
                      <span className="text-2xl font-bold tabular-nums tracking-tight text-white sm:text-3xl">
                        {fmtCount(confirmed.keiei!.stageCounts[key])}
                      </span>
                      <DeltaChip
                        delta={momDelta(
                          confirmed.keiei!.stageCounts[key],
                          previous?.keiei?.stageCounts[key],
                        )}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 📋 当月速報（月途中）+ 📈 ペース換算 + 🏷 ハイライト */}
            <div className="space-y-1 border-t border-slate-800 pt-3 text-sm">
              {partial?.keiei && partial !== confirmed && (
                <p className="text-slate-300">
                  📋 {shortMonthLabel(partial.period)}速報（月途中）: 受注{' '}
                  <span className="font-semibold tabular-nums text-white">
                    {fmtCount(partial.keiei.stageCounts.juchu)}
                  </span>{' '}
                  ・開通{' '}
                  <span className="font-semibold tabular-nums text-white">
                    {fmtCount(partial.keiei.stageCounts.activated)}
                  </span>{' '}
                  <span className="text-slate-500">（確定は翌月初）</span>
                </p>
              )}
              {pace && (
                <p className="text-slate-300">
                  📈 月末ペース換算: 受注{' '}
                  <span className="whitespace-nowrap font-semibold tabular-nums text-white">
                    約{fmtCount(pace.projected)}件
                  </span>{' '}
                  <span className="text-slate-500">
                    （{pace.dayOfMonth}日時点の単純換算・実働ベースの着地見込は日報参照）
                  </span>
                </p>
              )}
              {(highlights.orderTop || highlights.lossTop) && (
                <p className="text-slate-300">
                  🏷 当月 受注トップ{' '}
                  {highlights.orderTop && (
                    <span className="font-semibold text-white">
                      {highlights.orderTop.family}({fmtCount(highlights.orderTop.counts.juchu)})
                    </span>
                  )}
                  {highlights.lossTop && (
                    <>
                      {' ／ 離脱トップ '}
                      <span className="font-semibold text-white">
                        {highlights.lossTop.family}(
                        {fmtCount(
                          highlights.lossTop.counts.cancelled +
                            highlights.lossTop.counts.churned,
                        )}
                        )
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>

            {/* 矢印ファネル（全体 → 商材ファミリ topN + その他） */}
            <div className="border-t border-slate-800 pt-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-widest text-slate-500">
                【商材ファミリ別ファネル（{partial ? shortMonthLabel(partial.period) : '-'}・当月ベース速報）】
              </p>
              <div className="overflow-x-auto pb-1">
                {partial?.keiei && <FunnelLineRow label="全体" counts={partial.keiei.stageCounts} />}
                {families.length > 0 && <div className="my-1 border-t border-slate-800" />}
                {families.map((f) => (
                  <FunnelLineRow key={f.family} label={f.family} counts={f.counts} indent />
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                ()=受注比（率 = 件数 ÷ 受注件数）。分母 0 は「-%」。0 件のみの商材は非表示
              </p>
            </div>
          </div>
        )}
      </div>

      <SectionCard title="月次トレンド（受注 / 開通 / 入金）">
        {overviewQuery.isLoading ? (
          <LoadingRow label="月次トレンド" />
        ) : overviewQuery.error ? (
          <ErrorRow label="月次トレンド" />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {/* 施策イベント月を縦線で注釈（GR-04c-2 の可視化） */}
              {trendData
                .filter((d) => eventPeriods.has(d.period))
                .map((d) => (
                  <ReferenceLine key={d.period} x={d.period} stroke="#f59e0b" strokeDasharray="4 4" />
                ))}
              <Line type="monotone" dataKey="受注" stroke="#3b82f6" connectNulls />
              <Line type="monotone" dataKey="開通" stroke="#10b981" connectNulls />
              <Line type="monotone" dataKey="入金" stroke="#8b5cf6" connectNulls />
            </LineChart>
          </ResponsiveContainer>
        )}
      </SectionCard>

      {/* 月次テーブル: 当月ベース / 受注月ベース（日報 funnel-v2 と同じ用語・同じ 8 指標順） */}
      <SectionCard
        title="月次テーブル"
        aside={
          <div className="flex gap-1">
            {(
              [
                ['keiei', '当月ベース'],
                ['bunseki', '受注月ベース'],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setTableView(v)}
                className={cn(
                  'rounded-md border px-3 py-1 text-sm',
                  tableView === v
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'text-muted-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        }
        note={
          tableView === 'keiei' ? (
            <>
              <GlossaryTerm term="当月ベース">当月ベース</GlossaryTerm>
              {' = 各イベント日がその月の件数。前月比は最新月と前月の差（+増 / -減、比較先なしは -）'}
            </>
          ) : (
            <>
              <GlossaryTerm term="受注月ベース">受注月ベース</GlossaryTerm>
              {' = その月に受注した案件が現時点で各段階に到達した累計。若い月ほど未成熟（速報）'}
            </>
          )
        }
      >
        {overviewQuery.isLoading ? (
          <LoadingRow label="月次テーブル" />
        ) : overviewQuery.error ? (
          <ErrorRow label="月次テーブル" />
        ) : monthlyTable ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="whitespace-nowrap py-2 pr-4">指標</th>
                  {monthlyTable.columns.map((c) => (
                    <th key={c} className="whitespace-nowrap px-2 py-2 text-right">
                      {c}
                    </th>
                  ))}
                  <th className="whitespace-nowrap px-2 py-2 text-right">前月比</th>
                </tr>
              </thead>
              <tbody>
                {monthlyTable.rows.map((row) => (
                  <tr key={row.label} className="border-b last:border-0">
                    <td className="whitespace-nowrap py-2 pr-4 font-medium">
                      <GlossaryTerm term={row.label}>{row.label}</GlossaryTerm>
                    </td>
                    {row.cells.map((cell, i) => (
                      <td key={i} className="px-2 py-2 text-right tabular-nums">
                        {cell === null ? '-' : fmtCount(cell)}
                      </td>
                    ))}
                    <td
                      className={cn(
                        'px-2 py-2 text-right font-semibold tabular-nums',
                        row.delta.startsWith('+') && 'text-emerald-600',
                        row.delta.startsWith('-') && row.delta !== '-' && 'text-red-600',
                      )}
                    >
                      {row.delta}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </SectionCard>

      {/* ステージ内訳（棒グラフ対比） */}
      <SectionCard
        title="ステージ内訳"
        aside={
          <select
            value={effectivePeriod ?? ''}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="rounded-md border px-2 py-1 text-sm"
            aria-label="対象月"
          >
            {periods.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        }
        note="当月ベース = 各イベント日がその月の件数 ／ 受注月ベース = その月受注分の到達件数"
      >
        {overviewQuery.isLoading ? (
          <LoadingRow label="ステージ内訳" />
        ) : overviewQuery.error ? (
          <ErrorRow label="ステージ内訳" />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stageData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="stage" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="当月ベース" fill="#3b82f6" />
              <Bar dataKey="受注月ベース" fill="#93c5fd" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </SectionCard>

      {/* Segment 内訳（drill） */}
      <SectionCard
        title="Segment 内訳"
        aside={
          <div className="flex gap-1">
            {AXIS_TABS.map((t) => (
              <button
                key={t.axis}
                type="button"
                onClick={() => {
                  setAxis(t.axis)
                  setDrillProduct(null)
                }}
                className={cn(
                  'rounded-md border px-3 py-1 text-sm',
                  axis === t.axis
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'text-muted-foreground',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        }
        note="開通率 = 開通 ÷ 受注（受注比）。分母 0 は「-%」。全指標 0 の行は非表示"
      >
        {segmentsQuery.isLoading ? (
          <LoadingRow label="segment 集計" />
        ) : segmentsQuery.error ? (
          <ErrorRow label="segment 集計" />
        ) : (
          <>
            {axis === 'product' && (
              <p className="mb-2 text-xs text-muted-foreground">
                行をクリックするとリスト内訳に drill down します
              </p>
            )}
            <SegmentTable
              rows={segmentRows}
              nameHeader={axis === 'owner' ? '担当者' : '商材'}
              onRowClick={axis === 'product' ? (name) => setDrillProduct(name) : undefined}
            />
            {drillProduct !== null && (
              <div className="mt-4 rounded-md border bg-gray-50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold">{drillProduct} のリスト内訳</h3>
                  <button
                    type="button"
                    onClick={() => setDrillProduct(null)}
                    className="text-xs text-muted-foreground underline"
                  >
                    閉じる
                  </button>
                </div>
                {listSegmentsQuery.isLoading ? (
                  <LoadingRow label="リスト内訳" />
                ) : listSegmentsQuery.error ? (
                  <ErrorRow label="リスト内訳" />
                ) : (
                  <SegmentTable rows={drillRows} nameHeader="リスト" />
                )}
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* 施策イベント before/after */}
      <SectionCard title="施策イベントと開通率への影響">
        {policyEventsQuery.isLoading ? (
          <LoadingRow label="施策イベント" />
        ) : policyEventsQuery.error ? (
          <ErrorRow label="施策イベント" />
        ) : policyEvents.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            登録済みの施策イベントはありません（施策日を登録すると受注月ベース
            開通率の前後比較がここに表示されます）
          </p>
        ) : (
          <ul className="divide-y">
            {policyEvents.map((event) => (
              <li key={event.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-[12rem]">
                  <p className="font-medium">{event.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {event.eventDate}
                    {event.description ? ` — ${event.description}` : ''}
                  </p>
                </div>
                {event.comparison ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm tabular-nums">
                    <span>開通率 {event.comparison.beforeAverage}%</span>
                    <span className="text-muted-foreground">→</span>
                    <span>{event.comparison.afterAverage}%</span>
                    <span
                      className={cn(
                        'whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold',
                        event.comparison.diff >= 0
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700',
                      )}
                    >
                      {event.comparison.diff >= 0 ? '+' : ''}
                      {event.comparison.diff}pt
                    </span>
                    <span className="text-xs text-muted-foreground">
                      （前 <span className="whitespace-nowrap">{event.comparison.beforeCount}ヶ月</span> / 後{' '}
                      <span className="whitespace-nowrap">{event.comparison.afterCount}ヶ月</span>）
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    判定なし（比較には施策日の前後両方の受注月ベース開通率が必要です）
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* 対象範囲の但し書き（日報の末尾但し書きと同じ役割） */}
      <p className="text-xs text-muted-foreground">
        集計 = CH の月次 snapshot（Salesforce 直集計・集計値のみ保持）。当月は速報・確定は翌月初。
        実働時間ベースの効率・着地見込は毎朝の Slack 日報を参照
      </p>
    </div>
  )
}

// ─── Segment テーブル（開通率列つき） ───

function SegmentTable({
  rows,
  nameHeader,
  onRowClick,
}: {
  rows: ReadonlyArray<{ segmentKey: string; stageCounts: FunnelStageCounts; name: string }>
  nameHeader: string
  onRowClick?: (name: string) => void
}) {
  if (rows.length === 0) {
    return <p className="py-4 text-sm text-muted-foreground">この月の segment 集計はまだありません</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="whitespace-nowrap py-2 pr-4">{nameHeader}</th>
            {STAGE_DEFS.map((s) => (
              <th key={s.key} className="whitespace-nowrap px-2 py-2 text-right">
                {s.label}
              </th>
            ))}
            <th className="whitespace-nowrap px-2 py-2 text-right">開通率</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.segmentKey}
              className={cn('border-b last:border-0', onRowClick && 'cursor-pointer hover:bg-gray-50')}
              onClick={onRowClick ? () => onRowClick(row.name) : undefined}
            >
              <td className="max-w-[16rem] truncate py-2 pr-4 font-medium">{row.name}</td>
              {STAGE_DEFS.map((s) => (
                <td key={s.key} className="px-2 py-2 text-right tabular-nums">
                  {fmtCount(row.stageCounts[s.key])}
                </td>
              ))}
              <td className="px-2 py-2 text-right font-semibold tabular-nums">
                {okPct(row.stageCounts.activated, row.stageCounts.juchu)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
